import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import BackgroundDesign from "../components/BackgroundDesign";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import {
  faCalendarAlt,
  faMapMarkerAlt,
  faClock,
  faUsers,
  faHandHoldingHeart,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CampaignHeader = styled.div`
  position: relative;
  height: 100vh;
  width: 100%;
  padding-top: 120px; /* Added padding for desktop navbar height */

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
      url(${(props) => props.image}) center/cover no-repeat;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(
      to top,
      ${(props) => props.theme.colors.background},
      transparent
    );
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding-top: 80px; /* Adjusted for mobile navbar height */
    height: 75vh;
  }
`;

const HeaderContent = styled.div`
  position: absolute;
  bottom: 150px;
  left: 0;
  right: 0;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  color: white;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    bottom: 100px;
    padding: 0 1rem;
  }
`;

const BackButton = styled.button`
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(-5px);
  }
`;

const CampaignTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  padding: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CampaignMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${(props) => props.theme.colors.secondary};
  }
`;

const MainContent = styled.main`
  position: relative;
  z-index: 10;
  margin-top: -4rem;
  padding-bottom: 4rem;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 0 2rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const CampaignDetails = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: ${slideUp} 0.8s ease-out;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
`;

const Tab = styled.button`
  flex: 1;
  padding: 1.5rem 2rem;
  background: ${(props) => (props.active ? "white" : "#f9f9f9")};
  border: none;
  border-bottom: 3px solid
    ${(props) => (props.active ? props.theme.colors.primary : "transparent")};
  font-weight: ${(props) => (props.active ? "700" : "500")};
  color: ${(props) => (props.active ? props.theme.colors.text : "#777")};
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  white-space: nowrap;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.9rem;
    min-width: 100px;
  }
`;

const TabContent = styled.div`
  padding: 2.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Paragraph = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.8;
  color: #444;
  font-size: 1.05rem;

  strong {
    color: ${(props) => props.theme.colors.primary};
    font-weight: 700;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 2.5rem 0 1.5rem;
  position: relative;
  padding-left: 1rem;
  color: ${(props) => props.theme.colors.text};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${(props) => props.theme.colors.primary};
    border-radius: 10px;
  }
`;

const HighlightBox = styled.div`
  background: #f9f9f9;
  border-left: 4px solid ${(props) => props.theme.colors.secondary};
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0 10px 10px 0;
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
`;

const GalleryImage = styled.div`
  height: 150px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const UpdateItem = styled.div`
  background: #f9f9f9;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const UpdateDate = styled.div`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.secondary};
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const UpdateTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${(props) => props.theme.colors.text};
`;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 992px) {
    grid-row: 1;
  }
`;

const SidebarCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  animation: ${slideUp} 0.8s ease-out;
  animation-delay: 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const ProgressContainer = styled.div`
  margin-bottom: 2rem;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 1rem;

  h3 {
    font-size: 2rem;
    font-weight: 800;
    color: ${(props) => props.theme.colors.text};

    span {
      font-size: 1rem;
      opacity: 0.6;
      font-weight: 500;
    }
  }

  p {
    font-weight: 600;
    color: ${(props) => props.theme.colors.secondary};
  }
`;

const ProgressBar = styled.div`
  height: 10px;
  background: #eee;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressFill = styled.div`
  background: #b5873a;
  height: 100%;
  width: ${(props) => props.progress}%;
  border-radius: 10px;
  transition: width 1.5s ease;
`;

const CampaignStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatItem = styled.div`
  text-align: center;

  h4 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${(props) => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9rem;
    color: #666;
  }
`;

const DonateButton = styled.button`
  width: 100%;
  background: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 1.25rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px ${(props) => props.theme.colors.primary}33;
  }
`;

const SharingOptions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialButton = styled.button`
  background: #f2f2f2;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    background: ${(props) => props.theme.colors.secondary};
    color: white;
  }
`;

const actionsData = {
  "morocco-portugal-beach-cleanup": {
    id: 1,
    title: "We Cleaned. We Cared. We Protected",
    location: "Ain Sebaa Beach, Casablanca, Morocco",
    date: "June 22, 2025",
    image: `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup.webp`,
    raised: 0,
    goal: 0,
    donors: 0,
    daysLeft: 0,
    progress: 0,
    showStats: false,
    content: {
      overview: `On June 22, 2025, The Big Heart Association led an impactful environmental cleanup at Ain Sebaa Beach, Casablanca — a day that became much more than just removing waste.`,
      details: [
        {
          title: "International Environmental Solidarity",
          content: `Volunteers, members, and partners came together in a shared act of responsibility, restoring not only the beauty of the coastline but also our connection to nature. Each collected item symbolized a deeper purpose: respect, awareness, and change.

 We were deeply honored by the presence of representatives from APIP - Associação Portuguesa da Indústria de Plásticos, whose support brought a strong spirit of international collaboration.

Throughout the day, what stood out wasn’t just the visible change on the beach it was the real voices that resonated. From the Vice President of APIP to our own devoted members, everyone spoke from the heart, sharing reflections that reminded us of the strength of unity, the value of local action, and the beauty of human connection.

This event was a powerful reminder that protecting the environment is not just about effort it's about empathy, partnership, and listening to those who care deeply. We cleaned, we cared, we protected and we were heard.`,
        },
      ],
      conclusion: `Let's clean. Let's care. Let's protect. This powerful day of solidarity and positive impact represents more than an environmental action – it's a testament to what we can achieve when hearts unite for a common cause. Join us as we write a new chapter in Morocco-Portugal friendship while safeguarding our precious coastline for future generations. 🤝❤️💚♻️`,
    },
    updates: [
      {
        date: "January 15, 2025",
        title: "Partnership Announcement",
        content: "We're thrilled to announce our groundbreaking partnership with APIP - Associação Portuguesa da Indústria de Plásticos. This collaboration brings together Moroccan environmental passion with Portuguese industry expertise.",
      },
      {
        date: "February 1, 2025",
        title: "Volunteer Registration Opens",
        content: "Registration is now open for volunteers from both Morocco and Portugal. We're looking for passionate individuals who want to make a difference in coastal conservation and international environmental cooperation.",
      },
      {
        date: "February 20, 2025",
        title: "Educational Program Development",
        content: "Our team is developing comprehensive educational materials about marine conservation, plastic waste management, and sustainable practices. These will be shared with all participants during the event.",
      },
    ],
    gallery: [
      `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup/beach-1.webp`,
      `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup/beach-2.webp`,
      `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup/beach-3.webp`,
      `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup/beach-4.webp`,
      `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup/beach-5.webp`,
      `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup/beach-6.webp`,
      `${process.env.PUBLIC_URL}/images/OurActions/beach-cleanup/beach-7.webp`,
    ],
  },
  "pilot-project": {
    id: 2,
    title: "First Action: Pilot Project in Douar Al Byadna",
    location: "Marrakech-Safi Region, Morocco",
    date: "Nov 15-17, 2023",
    image: "/images/marrakech-safi.webp",
    raised: 25100,
    goal: 30000,
    donors: 75,
    daysLeft: 0,
    progress: 84,
    showStats: true,
    content: {
      overview: `From November 15 to 17, the Heartbeat Journey project took its first step in Douar Al Byadna, Errhamna, Marrakech-Safi, marking the beginning of a journey filled with hope, change, and new possibilities. This pilot action was much more than just a project, it was a renewal of dreams and opportunities for the children of this village.`,
      details: [
        {
          title: "Classroom Renovations",
          content: `We brought classrooms back to life, creating a space where students can learn, grow, and aspire to a brighter future. The renovations included repairing walls, installing new windows, enhancing lighting, and creating a more comfortable learning environment.`,
        },
        {
          title: "Technology Integration",
          content: `Computers were introduced, opening doors to knowledge, technology, and endless possibilities. By bringing digital tools into the classroom, we're helping bridge the digital divide and preparing students for the modern world.`,
        },
        {
          title: "Educational Supplies",
          content: `Essential school supplies were offered to every child, ensuring that they have everything they need to write their own success stories. From notebooks and pens to textbooks and art supplies, each student received the tools needed for effective learning.`,
        },
        {
          title: "Interactive Learning Activities",
          content: `Beyond the physical improvements, we enriched the learning experience by organizing training and interactive activities, making education more dynamic, engaging, and joyful. It was a time of sharing, laughter, and inspiration, reminding us that true change begins with education and empowerment.`,
        },
      ],
      conclusion: `This first step is just the beginning of a transformative journey, where every action, no matter how small, contributes to building a better future for Morocco's young generations. With every school we renovate and every child we support, we are shaping a world where education, hope, and opportunity reach every corner. The Heartbeat Journey has begun, and the best is yet to come.`,
    },
    updates: [
      {
        date: "Nov 17, 2023",
        title: "Pilot Project Successfully Completed",
        content:
          "We're proud to announce the successful completion of our pilot project in Douar Al Byadna! Over three intensive days, our team renovated the local school, installed computer equipment, and distributed educational supplies to all students.",
      },
      {
        date: "Nov 30, 2023",
        title: "Impact Assessment Results",
        content:
          "Our follow-up assessment shows significant improvements in student attendance and engagement following the renovations. Teachers report that the new technology is already enhancing the learning experience for students of all ages.",
      },
      {
        date: "Dec 15, 2023",
        title: "Planning Next Steps",
        content:
          "Based on the success of our pilot project, we're now finalizing plans for our comprehensive 7-year Heartbeat Journey initiative. Stay tuned for exciting announcements about our expansion to all 12 regions of Morocco.",
      },
      {
        date: "Jan 20, 2024",
        title: "Partnership Announcement",
        content:
          "We're thrilled to announce new partnerships with three major educational foundations that will help us scale our impact. These collaborations will provide additional resources, expertise, and funding to support our mission.",
      },
    ],
    gallery: [
      "/images/IMG_7663.JPG",
      "/images/IMG_7646.JPG",
      "/images/IMG_7594.JPG",
      "/images/IMG_7398.jpg",
      "/images/photo_5802988364859820042_y.jpg",
      "/images/photo_5802988364859820039_y.jpg",
      "/images/photo_5802988364859820038_y.jpg",
      "/images/photo_5802988364859820040_y.jpg",
    ],
  },
};

const CampaignDetailPage = () => {
  const { actionId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState("about");

  const action = actionsData[actionId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!action) {
    return <Navigate to="/actions" />;
  }

  // Determine if we should show stats based on the action's showStats property
  const showStats = actionsData[actionId].showStats !== false;

  return (
    <PageWrapper>
      <BackgroundDesign />

      <CampaignHeader image={action.image}>
        <HeaderContent>
          <BackButton onClick={() => navigate("/actions")}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to actions
          </BackButton>
          <CampaignTitle>{action.title}</CampaignTitle>
          <CampaignMeta>
            <MetaItem>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>{action.location}</span>
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{action.date}</span>
            </MetaItem>
            {showStats && action.daysLeft > 0 && (
              <MetaItem>
                <FontAwesomeIcon icon={faClock} />
                <span>{action.daysLeft} days left</span>
              </MetaItem>
            )}
          </CampaignMeta>
        </HeaderContent>
      </CampaignHeader>

      <MainContent>
        <ContentContainer>
          <CampaignDetails>
            <TabsContainer>
              <Tab
                active={activeTab === "about"}
                onClick={() => setActiveTab("about")}
              >
                About
              </Tab>
              {/* <Tab
                active={activeTab === "updates"}
                onClick={() => setActiveTab("updates")}
              >
                Updates
              </Tab> */}
              <Tab
                active={activeTab === "gallery"}
                onClick={() => setActiveTab("gallery")}
              >
                Gallery
              </Tab>
            </TabsContainer>

            <TabContent>
              {activeTab === "about" && (
                <>
                  <Paragraph>
                    <strong>{action.content.overview}</strong>
                  </Paragraph>

                  {action.content.details.map((section, index) => (
                    <div key={index}>
                      <SectionTitle>{section.title}</SectionTitle>
                      <Paragraph>{section.content}</Paragraph>
                    </div>
                  ))}

                  <HighlightBox>
                    <Paragraph>
                      <strong>{action.content.conclusion}</strong>
                    </Paragraph>
                  </HighlightBox>
                </>
              )}

              {activeTab === "updates" && (
                <>
                  <SectionTitle>Latest Updates</SectionTitle>
                  {action.updates && action.updates.length > 0 ? (
                    action.updates.map((update, index) => (
                      <UpdateItem key={index}>
                        <UpdateDate>{update.date}</UpdateDate>
                        <UpdateTitle>{update.title}</UpdateTitle>
                        <Paragraph>{update.content}</Paragraph>
                      </UpdateItem>
                    ))
                  ) : (
                    <Paragraph>
                      No updates available yet. Check back soon for news about
                      this campaign!
                    </Paragraph>
                  )}
                  <Paragraph>
                    Follow us on social media to get real-time updates about all
                    our activities and how you can contribute to make this
                    campaign a success.
                  </Paragraph>
                </>
              )}

              {activeTab === "gallery" && (
                <>
                  <SectionTitle>Campaign Gallery</SectionTitle>
                  {/* <Paragraph>
                    No images available yet. Check back soon for photos from
                    this campaign!
                  </Paragraph> */}
                  <ImageGallery>
                    {action.gallery.map((image, index) => (
                      <GalleryImage key={index}>
                        <img
                          src={image}
                          alt={`Campaign gallery ${index + 1}`}
                        />
                      </GalleryImage>
                    ))}
                  </ImageGallery>
                </>
              )}
            </TabContent>
          </CampaignDetails>

          <SidebarContainer>
            <SidebarCard>
              {showStats ? (
                <>
                  <ProgressContainer>
                    <ProgressHeader>
                      <h3>
                        MAD{action.raised.toLocaleString()}{" "}
                        <span>raised of MAD{action.goal.toLocaleString()}</span>
                      </h3>
                      <p>{action.progress}%</p>
                    </ProgressHeader>
                    <ProgressBar>
                      <ProgressFill progress={action.progress} />
                    </ProgressBar>
                  </ProgressContainer>

                  <CampaignStats>
                    <StatItem>
                      <h4>{action.donors}</h4>
                      <p>Donors</p>
                    </StatItem>
                    <StatItem>
                      <h4>{action.daysLeft}</h4>
                      <p>Days Left</p>
                    </StatItem>
                    <StatItem>
                      <h4>1</h4>
                      <p>School</p>
                    </StatItem>
                  </CampaignStats>
                </>
              ) : (
                <>
                  <SectionTitle>Support This Initiative</SectionTitle>
                  <Paragraph>
                    Your contribution helps us continue this important work.
                    Every donation brings us one step closer to creating
                    meaningful change.
                  </Paragraph>
                </>
              )}
              <Link to="/donate">
                <DonateButton>Donate Now</DonateButton>
              </Link>

              <SharingOptions>
                <SocialButton>f</SocialButton>
                <SocialButton>t</SocialButton>
                <SocialButton>in</SocialButton>
              </SharingOptions>
            </SidebarCard>

            <SidebarCard>
              <SectionTitle>Campaign Team</SectionTitle>
              <Paragraph>
                This campaign is led by our dedicated members and staff who
                are passionate about creating positive change in Morocco.
              </Paragraph>
              <Paragraph>
                <strong>Contact:</strong> asso.thebigheart@tbh.ma
              </Paragraph>
            </SidebarCard>
          </SidebarContainer>
        </ContentContainer>
      </MainContent>
    </PageWrapper>
  );
};

export default CampaignDetailPage;
